import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormControl,
  FormLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const CustomerRequestApproval = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState(''); // 'approve' or 'reject'
  const [comments, setComments] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [nicFilter, setNicFilter] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/admin/customer-requests/pending');
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const handleApprove = async () => {
    try {
      // For UPDATE requests, admin MUST select at least one field
      if (selectedRequest.requestType === 'UPDATE' && selectedFields.length === 0) {
        alert('Please select at least one field to allow editing');
        return;
      }
      
      const params = { adminId: user.userId };
      
      // For UPDATE requests, always send allowedFields (admin must select fields)
      if (selectedRequest.requestType === 'UPDATE') {
        params.allowedFields = JSON.stringify(selectedFields);
      }
      
      await api.post(`/admin/customer-requests/${selectedRequest.id}/approve`, null, { params });
      fetchRequests();
      setOpen(false);
      setSelectedRequest(null);
      setComments('');
      setSelectedFields([]);
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Error approving request: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      alert('Please provide rejection comments');
      return;
    }
    try {
      await api.post(`/admin/customer-requests/${selectedRequest.id}/reject`, null, {
        params: {
          adminId: user.userId,
          comments: comments,
        },
      });
      fetchRequests();
      setOpen(false);
      setSelectedRequest(null);
      setComments('');
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error rejecting request: ' + (error.response?.data?.message || error.message));
    }
  };

  const openDialog = (request, actionType) => {
    setSelectedRequest(request);
    setAction(actionType);
    setComments('');
    setSelectedFields([]);
    setOpen(true);
  };
  
  // Customer field definitions for selection
  const customerFields = [
    { key: 'fullName', label: 'Full Name' },
    { key: 'address', label: 'Address' },
    { key: 'businessAddress', label: 'Business Address' },
    { key: 'gramaNiladhariDivision', label: 'Grama Niladhari Division' },
    { key: 'areaId', label: 'Area' },
    { key: 'nic', label: 'NIC' },
    { key: 'birthdate', label: 'Birthdate' },
    { key: 'occupation', label: 'Occupation' },
    { key: 'registrationPeriod', label: 'Registration Period' },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'spouseFullName', label: 'Spouse Full Name' },
    { key: 'spouseNic', label: 'Spouse NIC' },
    { key: 'spousePhoneNumber', label: 'Spouse Phone Number' },
    { key: 'numberOfDependents', label: 'Number of Dependents' },
    { key: 'assetsLands', label: 'Assets - Lands' },
    { key: 'assetsVehicles', label: 'Assets - Vehicles' },
    { key: 'assetsOther', label: 'Assets - Other' },
    { key: 'district', label: 'District' },
    { key: 'bankAccountNumber', label: 'Bank Account Number' },
    { key: 'bankBranch', label: 'Bank Branch' },
    { key: 'bankName', label: 'Bank Name' },
    { key: 'guarantorName', label: 'Guarantor Name' },
    { key: 'guarantorAddress', label: 'Guarantor Address' },
    { key: 'guarantorBusinessAddress', label: 'Guarantor Business Address' },
    { key: 'guarantorOccupation', label: 'Guarantor Occupation' },
    { key: 'guarantorNic', label: 'Guarantor NIC' },
    { key: 'guarantorPhoneNumber', label: 'Guarantor Phone Number' },
  ];
  
  const handleFieldToggle = (fieldKey) => {
    setSelectedFields(prev => 
      prev.includes(fieldKey) 
        ? prev.filter(f => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'APPROVED_FOR_EDIT':
        return 'info';
      case 'COMPLETED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Filter requests by NIC
  const filteredRequests = requests.filter((request) => {
    if (!nicFilter.trim()) return true;
    const customer = request.customer;
    const nic = customer?.nic || '';
    return nic.toLowerCase().includes(nicFilter.toLowerCase().trim());
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Officer's Requests
      </Typography>
      
      {/* NIC Filter */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Filter by NIC"
          variant="outlined"
          value={nicFilter}
          onChange={(e) => setNicFilter(e.target.value)}
          placeholder="Enter NIC to search"
          sx={{ minWidth: 300 }}
        />
      </Box>
      
      <TableContainer 
        component={Paper} 
        sx={{ 
          mt: 2,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
          overflowX: 'auto',
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#0D47A1',
            borderRadius: 4,
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Request ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Customer</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Request Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Requested By</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Reason</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Requested Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {nicFilter ? 'No requests found matching the NIC filter.' : 'No pending requests found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{request.id}</TableCell>
                <TableCell>
                  {request.customer?.fullName || request.customer?.name} ({request.customer?.nic})
                </TableCell>
                <TableCell>
                  <Chip
                    label={request.requestType}
                    color={request.requestType === 'DELETE' ? 'error' : 'primary'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{request.requestedBy?.fullName}</TableCell>
                <TableCell>{request.reason || 'N/A'}</TableCell>
                <TableCell>
                  <Chip
                    label={request.status}
                    color={getStatusColor(request.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {request.requestedAt
                    ? new Date(request.requestedAt).toLocaleDateString()
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => openDialog(request, 'approve')}
                    sx={{ mr: 1 }}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => openDialog(request, 'reject')}
                  >
                    Reject
                  </Button>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <DialogTitle>
          {action === 'approve' ? 'Approve' : 'Reject'} Customer {selectedRequest?.requestType} Request
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Request Details</Typography>
                  <Typography><strong>Customer:</strong> {selectedRequest.customer?.fullName || selectedRequest.customer?.name}</Typography>
                  <Typography><strong>NIC:</strong> {selectedRequest.customer?.nic}</Typography>
                  <Typography><strong>Request Type:</strong> {selectedRequest.requestType}</Typography>
                  <Typography><strong>Requested By:</strong> {selectedRequest.requestedBy?.fullName}</Typography>
                  <Typography><strong>Reason:</strong> {selectedRequest.reason || 'N/A'}</Typography>
                </CardContent>
              </Card>

              {selectedRequest.requestType === 'UPDATE' && selectedRequest.updatedData && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Updated Customer Data</Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(JSON.parse(selectedRequest.updatedData), null, 2)}
                      </pre>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {action === 'reject' && (
                <TextField
                  fullWidth
                  label="Rejection Comments (Required)"
                  multiline
                  rows={4}
                  required
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  margin="normal"
                  placeholder="Please provide a reason for rejecting this request..."
                />
              )}

              {action === 'approve' && selectedRequest.requestType === 'UPDATE' && (
                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>Important:</strong> You must select which fields the officer is allowed to edit. 
                      Only the selected fields will be editable by the officer.
                    </Alert>
                    
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend">
                        <strong>Select Fields to Allow for Editing (Required)</strong>
                      </FormLabel>
                      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #ccc', borderRadius: 1, p: 2, mt: 1 }}>
                        <Grid container spacing={1}>
                          {customerFields.map((field) => (
                            <Grid item xs={12} sm={6} md={4} key={field.key}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedFields.includes(field.key)}
                                    onChange={() => handleFieldToggle(field.key)}
                                    size="small"
                                  />
                                }
                                label={field.label}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                      {selectedFields.length === 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <strong>Warning:</strong> Please select at least one field to allow editing. 
                          The officer will only be able to edit the fields you select.
                        </Alert>
                      )}
                      {selectedFields.length > 0 && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                          <strong>Selected {selectedFields.length} field(s):</strong> {selectedFields.map(key => {
                            const field = customerFields.find(f => f.key === key);
                            return field ? field.label : key;
                          }).join(', ')}
                        </Alert>
                      )}
                    </FormControl>
                  </CardContent>
                </Card>
              )}

              {action === 'approve' && selectedRequest.requestType === 'DELETE' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Warning:</strong> Approving this request will permanently delete the customer record. This action cannot be undone.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {action === 'approve' && (
            <Button 
              onClick={handleApprove} 
              variant="contained" 
              color="success"
              disabled={selectedRequest?.requestType === 'UPDATE' && selectedFields.length === 0}
            >
              Approve Request
            </Button>
          )}
          {action === 'reject' && (
            <Button
              onClick={handleReject}
              variant="contained"
              color="error"
              disabled={!comments.trim()}
            >
              Reject
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerRequestApproval;

